import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, RefreshCcw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useApp } from "@/App";
import LuckyTickets from "@/components/LuckyTickets";
import { cn } from "@/lib/utils";

const DEFAULT_TICKET_MULTIPLIERS = [0.01, 0.25, 0.5, 0.75, 1, 1.5, 2, 5, 10, 25, 50, 100, 500, 1000];

const FortuneWheelPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { haptic, api, balances, refreshData, isTelegramWebApp, setBackHandler, clearBackHandler } = useApp();
    const showInlineBack = !isTelegramWebApp;

    const [isFetching, setIsFetching] = useState(false);
    const [spinning, setSpinning] = useState(false);
    const [lastResult, setLastResult] = useState(null);
    const [spinId, setSpinId] = useState(0);
    const [loading, setLoading] = useState(true);
    const [ticketMultipliers, setTicketMultipliers] = useState(DEFAULT_TICKET_MULTIPLIERS);

    // CENTRALIZED SCALING LOGIC
    const [scale, setScale] = useState(1);
    const rouletteContainerRef = useRef(null);
    const spinLockRef = useRef(false);

    useEffect(() => {
        const handleResize = () => {
            if (rouletteContainerRef.current) {
                const containerH = rouletteContainerRef.current.clientHeight;
                const containerW = rouletteContainerRef.current.clientWidth;

                if (containerH > 0 && containerW > 0) {
                    // Logic mirrored from previous LuckyTickets internal logic
                    // We need (5 * ticketH_actual) + (4 * gap_actual) <= containerH
                    // containerH = H * (5 + 0.48) = 5.48 * H
                    // H_actual = containerH / 5.48
                    const VISIBLE_TICKETS = 5;
                    const BASE_TICKET_W = 270;
                    const BASE_TICKET_H = 100;
                    const BASE_GAP = 12;

                    const rawHeight = containerH / (VISIBLE_TICKETS + (VISIBLE_TICKETS - 1) * (BASE_GAP / BASE_TICKET_H));
                    // Account for 80px total horizontal padding (40px each side) for shadows/glows
                    const availableW = containerW - (80 * (containerH / 548));
                    const derivedWidth = rawHeight * (BASE_TICKET_W / BASE_TICKET_H);

                    let finalScale;
                    if (derivedWidth > availableW) {
                        finalScale = availableW / BASE_TICKET_W;
                    } else {
                        finalScale = rawHeight / BASE_TICKET_H;
                    }
                    setScale(finalScale);
                }
            }
        };

        handleResize();
        const observer = new ResizeObserver(handleResize);
        if (rouletteContainerRef.current) observer.observe(rouletteContainerRef.current);
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
            observer.disconnect();
        };
    }, []);

    const loadFortuneData = useCallback(async () => {
        try {
            const response = await api.get("/fortune/config");
            const prizes = Array.isArray(response?.data?.prizes) ? response.data.prizes : [];
            const multipliers = [...new Set(
                prizes
                    .map((item) => Number(item?.multiplier))
                    .filter((value) => Number.isFinite(value))
            )];
            if (multipliers.length > 0) {
                setTicketMultipliers(multipliers);
            }
        } catch (error) {
            console.error("Error loading fortune data:", error);
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        loadFortuneData();
    }, [loadFortuneData]);

    const handleBack = useCallback(() => {
        haptic("light");
        navigate(-1);
    }, [haptic, navigate]);

    useEffect(() => {
        if (!isTelegramWebApp) return;
        setBackHandler(handleBack);
        return () => clearBackHandler();
    }, [isTelegramWebApp, setBackHandler, clearBackHandler, handleBack]);

    const getCurrencyBalance = useCallback((currency) => {
        const balance = balances.find((b) => b.currency === currency);
        return balance?.usd_value || 0;
    }, [balances]);

    const canAffordSpin = useCallback(() => {
        return getCurrencyBalance("USDT") >= 1.0;
    }, [getCurrencyBalance]);

    const handleSpin = useCallback(async () => {
        if (spinLockRef.current || isFetching || spinning || !canAffordSpin()) {
            return;
        }
        spinLockRef.current = true;

        haptic("medium");
        setIsFetching(true);
        setLastResult(null);

        try {
            const response = await api.post("/fortune/spin", {
                currency: "USDT",
            });

            if (response.data.success) {
                const result = response.data;
                setLastResult(result);
                setSpinId(prev => prev + 1);
                setSpinning(true);
                setIsFetching(false);

                setTimeout(() => {
                    refreshData();
                }, 4700);
            } else {
                setIsFetching(false);
                spinLockRef.current = false;
            }
        } catch (error) {
            console.error("Spin error:", error);
            alert(error.response?.data?.detail || "Error spinning wheel");
            setIsFetching(false);
            setSpinning(false);
            spinLockRef.current = false;
        }
    }, [isFetching, spinning, canAffordSpin, api, refreshData, haptic]);

    const handleSpinComplete = useCallback(() => {
        haptic(lastResult?.result_multiplier > 0 ? "heavy" : "light");

        setTimeout(() => {
            setSpinning(false);
            spinLockRef.current = false;
        }, 500);
    }, [lastResult, haptic]);

    const handleSpinKeyDown = useCallback((event) => {
        if (event.key === " " || event.key === "Spacebar") {
            event.preventDefault();
            if (!event.repeat) {
                handleSpin();
            }
        }
    }, [handleSpin]);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#1c242d] flex items-center justify-center">
                <div className="text-white/40 font-bold tracking-[0.2em]">LOADING...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen text-foreground flex flex-col animate-fade-in safe-bottom font-sans overflow-hidden" data-testid="fortune-page">
            {/* Holiday Wallpaper Background */}
            <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
                <div className="absolute inset-0 opacity-[0.08] dark:opacity-[0.12]">
                    <svg width="100%" height="100%" className="fill-current text-foreground/50">
                        <pattern id="tg-pattern" x="0" y="0" width="120" height="120" patternUnits="userSpaceOnUse">
                            <path d="M10,10 h15 v15 h-15 z" fill="none" stroke="currentColor" strokeWidth="1" />
                            <path d="M50,20 q5,-10 10,0 q5,10 -5,10 q-5,0 -5,-10" fill="none" stroke="currentColor" strokeWidth="1" />
                            <circle cx="90" cy="30" r="4" fill="none" stroke="currentColor" strokeWidth="1" />
                            <path d="M30,70 l5,5 l5,-5 l-5,-5 z" fill="none" stroke="currentColor" strokeWidth="1" />
                            <path d="M70,80 v15 h8 v-15 z" fill="none" stroke="currentColor" strokeWidth="1" />
                            <path d="M100,90 l10,-10 m0,10 l-10,-10" stroke="currentColor" strokeWidth="1" />
                        </pattern>
                        <rect width="100%" height="100%" fill="url(#tg-pattern)" />
                    </svg>
                </div>
            </div>

            {/* Header */}
            <div className="lp-bar lp-topbar sticky top-0 z-30 px-4 py-3 grid grid-cols-[2rem_1fr_2rem] items-center border-b border-[var(--lp-hair)]">
                {showInlineBack ? (
                    <button
                        type="button"
                        onClick={handleBack}
                        className="inline-flex h-8 w-8 items-center justify-center text-foreground"
                        aria-label="Back"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                ) : (
                    <span aria-hidden="true" className="h-8 w-8" />
                )}
                <h1 className="text-center font-bold text-[17px] text-foreground">{t("fortune.title")}</h1>
                <span aria-hidden="true" className="h-8 w-8" />
            </div>

            <div className="flex-1 overflow-hidden relative z-10 flex flex-col items-center justify-center pb-0 w-full max-h-[calc(100vh-60px)]">
                {/* Roulette Section with Arrows */}
                <div className="relative w-full flex-1 flex justify-center items-center overflow-hidden min-h-0">
                    {/* Aspect Ratio Container - Maximized to fill height */}
                    <div
                        className="relative shadow-2xl"
                        style={{
                            aspectRatio: '270/548',
                            maxHeight: '100%',
                            maxWidth: '63vw',
                            width: '100%'
                        }}
                        ref={rouletteContainerRef}
                    >
                        {/* Symmetrical Blue Pointers */}
                        <div
                            className="absolute top-1/2 left-0 z-40 flex items-center"
                            style={{ transform: `translate(calc(-100% - ${24 * scale}px), -50%) scale(${scale})` }}
                        >
                            <div className="w-0 h-0 border-l-[20px] border-l-[#34aadc] border-y-[15px] border-y-transparent drop-shadow-md" />
                        </div>
                        <div
                            className="absolute top-1/2 right-0 z-40 flex items-center"
                            style={{ transform: `translate(calc(100% + ${24 * scale}px), -50%) scale(${scale}) rotate(180deg)` }}
                        >
                            <div className="w-0 h-0 border-l-[20px] border-l-[#34aadc] border-y-[15px] border-y-transparent drop-shadow-md" />
                        </div>

                        {/* The Large Reel */}
                        <div className={cn(
                            "w-full h-full transition-all duration-1000 ease-out relative bg-transparent overflow-hidden rounded-xl px-10",
                            spinning ? "scale-[1.01]" : ""
                        )}>
                            <LuckyTickets
                                targetMultiplier={lastResult?.result_multiplier}
                                onSpinComplete={handleSpinComplete}
                                spinId={spinId}
                                scale={scale}
                                multipliers={ticketMultipliers}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Controls - Moved to high-level to avoid parent blur/z-index traps */}
            <div className={cn(
                "w-full transition-all duration-500 relative pb-6 pt-2 flex-shrink-0 flex justify-center px-4",
                (spinning || isFetching) ? "opacity-30 pointer-events-none z-50" : "z-50",
                (lastResult && !spinning && !isFetching) ? "z-[80]" : ""
            )}>
                <button
                    onClick={handleSpin}
                    onKeyDown={handleSpinKeyDown}
                    disabled={isFetching || spinning || !canAffordSpin()}
                    className={cn(
                        "group rounded-2xl font-black tracking-wider uppercase transition-all duration-300 shadow-[0_10px_40px_rgba(52,170,220,0.4)] active:scale-95 flex items-center justify-center border-b-4 border-[#2488b3]",
                        (isFetching || spinning || !canAffordSpin())
                            ? "bg-black/5 text-black/20 dark:bg-white/5 dark:text-white/20 border-b-0 shadow-none"
                            : "bg-[#34aadc] text-white hover:brightness-110 hover:-translate-y-0.5 active:translate-y-0.5"
                    )}
                    style={{
                        width: Math.min(320, 320 * scale),
                        height: 76 * scale,
                        fontSize: `${Math.round(24 * scale)}px`
                    }}
                >
                    {isFetching ? "..." : `SPIN $1`}
                </button>
            </div>

            {/* Win Backdrop & Notification Overlay - Moved to top level for full coverage */}
            {lastResult && !spinning && !isFetching && (
                <>
                    {/* Darkening Backdrop - FIXED screen cover with Pointer Events disabled for click-through */}
                    <div className="fixed inset-[-100px] bg-black/60 backdrop-blur-[2px] z-[60] animate-in fade-in duration-500 pointer-events-none" />

                    {/* Premium Result Notification - Pointer events none on outer wrapper to allow click-through */}
                    <div className="fixed top-[38%] left-0 w-full flex justify-center z-[70] pointer-events-none">
                        <div className={cn(
                            "px-12 py-7 rounded-[2rem] text-center font-black transition-all duration-1000 transform animate-in zoom-in-95 fade-in slide-in-from-bottom-20",
                            "bg-gradient-to-br from-[#0c121e] via-[#1a2b4b] to-[#0c121e] text-white",
                            "border-[3px] border-[#ffd700] shadow-[0_0_50px_rgba(255,215,0,0.3)] shimmer-rainbow glow-pulse",
                            "relative overflow-hidden"
                        )}
                            style={{ transform: `scale(${scale * 1.1})` }}
                        >
                            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-[#ffd700]/10 to-transparent animate-shimmer-fast pointer-events-none" />

                            <div className="relative z-10 flex flex-col items-center gap-2">
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full bg-[#ffd700] animate-ping" />
                                    <span className="text-[13px] uppercase tracking-[0.6em] text-[#ffd700] font-black">YOU WON!</span>
                                    <div className="w-2 h-2 rounded-full bg-[#ffd700] animate-ping" />
                                </div>
                                <span className="text-5xl tracking-tighter text-white drop-shadow-[0_2px_10px_rgba(255,255,255,0.3)]">
                                    +{lastResult.result_amount_usd} USDT
                                </span>
                                <div className="mt-1 px-4 py-1 rounded-full bg-[#ffd700] text-[#0c121e] text-[10px] font-bold tracking-widest uppercase">
                                    Lucky Spin
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            <style jsx>{`
                .animate-spin-slow {
                    animation: spin 4s linear infinite;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                
                :global(body) {
                    background-color: hsl(var(--background)) !important;
                    overflow: hidden;
                }
            `}</style>
        </div>
    );
};


export default FortuneWheelPage;
